/** UI/UX enhancements: filters, loading, prefetch, drafts, keyboard, swipe. */
import { esc, STATUS_LABELS, STATUS_BUCKETS, getPipeline, isClosedStatus, isLiteModeOn, setLiteMode, initLiteModeAssets } from './utils.js';

const STAGE_MODAL_STATUSES = new Set([
  'new', 'client_not_interested', 'quotation', 'quotation_sent',
  'visa_applied', 'visa_issued', 'details_provided',
  'temporary_booking', 'booking_confirmed', 'ticket_issued',
  'tour_accepted_advance', 'tour_accepted_progress', 'tour_finalised',
]);

const FILTER_PREFIX = 'mysky_filter_';
const DRAFT_PREFIX = 'mysky_draft_';

export function filterStorageKey(agentId) {
  return `${FILTER_PREFIX}${agentId || 'anon'}`;
}

export function saveFilterForAgent(agentId, filter, page = 1) {
  if (!agentId) return;
  try {
    localStorage.setItem(filterStorageKey(agentId), JSON.stringify({
      filter: { ...filter, q: '' },
      page,
    }));
  } catch { /* ignore */ }
}

export function loadFilterForAgent(agentId) {
  if (!agentId) return null;
  try {
    const raw = localStorage.getItem(filterStorageKey(agentId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function draftStorageKey(agentId) {
  return `${DRAFT_PREFIX}${agentId || 'anon'}`;
}

export function saveInquiryDraft(agentId, data) {
  if (!agentId) return;
  try {
    localStorage.setItem(draftStorageKey(agentId), JSON.stringify({ ...data, savedAt: Date.now() }));
  } catch { /* ignore */ }
}

export function loadInquiryDraft(agentId) {
  if (!agentId) return null;
  try {
    const raw = localStorage.getItem(draftStorageKey(agentId));
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (Date.now() - (d.savedAt || 0) > 7 * 86400000) {
      localStorage.removeItem(draftStorageKey(agentId));
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function clearInquiryDraft(agentId) {
  try { localStorage.removeItem(draftStorageKey(agentId)); } catch { /* ignore */ }
}

export function collectInquiryDraftFields() {
  const ids = [
    'm_Name', 'm_Phone', 'm_Email', 'm_Passport', 'm_PaxAdt', 'm_PaxStu', 'm_PaxChd', 'm_PaxInf',
    'm_RateAdt', 'm_RateStu', 'm_RateChd', 'm_RateInf', 'm_ProfitAdt', 'm_ProfitStu', 'm_ProfitChd', 'm_ProfitInf', 'm_Source', 'm_Package', 'm_InquiryDate',
    'm_Followup', 'm_Note', 'm_TicketIntakeDetails', 'm_Origin', 'm_Destination', 'm_Depart', 'm_Return',
    'm_TripType', 'm_CustomReq', 'm_TourN', 'm_TourD', 'm_VisaCost', 'm_TicketCost',
  ];
  const fields = {};
  ids.forEach((id) => {
    const el = document.getElementById(id);
    if (el) fields[id] = el.type === 'checkbox' ? el.checked : el.value;
  });
  fields.m_Type = document.getElementById('m_Type')?.value || 'tour';
  fields.m_Custom = document.getElementById('m_Custom')?.checked || false;
  return fields;
}

export function applyInquiryDraftFields(fields) {
  if (!fields) return;
  Object.entries(fields).forEach(([id, val]) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (el.type === 'checkbox') el.checked = !!val;
    else el.value = val ?? '';
  });
}

export function setListLoading(loading) {
  const panel = document.getElementById('tablePanel');
  if (panel) panel.classList.toggle('is-loading', loading);
  const overlay = document.getElementById('tableLoading');
  if (overlay) overlay.classList.toggle('hidden', !loading);
}

export function getNextStatus(type, current) {
  if (type === 'ticket' && current === 'booking_confirmed') return 'ticket_issued';
  const pipe = getPipeline(type);
  const idx = pipe.indexOf(current);
  if (idx < 0 || idx >= pipe.length - 1) return null;
  const next = pipe[idx + 1];
  if (isClosedStatus(next)) return null;
  return next;
}

export function canQuickAdvance(type, nextStatus) {
  if (STAGE_MODAL_STATUSES.has(nextStatus)) return false;
  if (type === 'visa' && nextStatus === 'details_provided') return false;
  return true;
}

export function buildFilterChips(filter, agents, deps) {
  const chips = [];
  const add = (key, label, clearFn) => {
    chips.push({ key, label, clear: clearFn });
  };

  if (filter.q?.trim()) {
    add('q', `Search: “${filter.q.trim()}”`, () => {
      const inp = document.getElementById('searchInput') || document.getElementById('leadsSearch');
      if (inp) inp.value = '';
      filter.q = '';
    });
  }
  if (filter.type) {
    add('type', filter.type.charAt(0).toUpperCase() + filter.type.slice(1), () => { filter.type = ''; });
  }
  if (filter.status) {
    const label = STATUS_LABELS[filter.status] || filter.status;
    add('status', label, () => { filter.status = ''; });
  }
  if (filter.bucket) {
    const label = STATUS_BUCKETS[filter.bucket]?.label || filter.bucket;
    add('bucket', label, () => { filter.bucket = ''; });
  }
  if (filter.overdue) add('overdue', 'Overdue', () => { filter.overdue = false; });
  if (filter.booking_expired) add('booking_expired', 'Expired bookings', () => { filter.booking_expired = false; });
  if (filter.custom) add('custom', 'Custom', () => { filter.custom = false; });
  if (filter.agent_id) {
    const ag = agents.find((a) => a.id === filter.agent_id);
    add('agent', ag ? ag.name : 'Agent', () => { filter.agent_id = ''; });
  }
  return chips;
}

export function renderFilterChips(filter, agents, onChange) {
  const box = document.getElementById('filterChips');
  if (!box) return;
  const chips = buildFilterChips(filter, agents, null);
  if (!chips.length) {
    box.classList.add('hidden');
    box.innerHTML = '';
    return;
  }
  box.classList.remove('hidden');
  box.innerHTML = chips.map((c) =>
    `<button type="button" class="filter-chip" data-chip-key="${esc(c.key)}" aria-label="Remove ${esc(c.label)} filter">${esc(c.label)} <span aria-hidden="true">×</span></button>`
  ).join('') + `<button type="button" class="filter-chip filter-chip-clear" data-chip-clear="1">Clear all</button>`;

  box.querySelectorAll('[data-chip-key]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const chip = chips.find((c) => c.key === btn.dataset.chipKey);
      chip?.clear();
      onChange();
    });
  });
  box.querySelector('[data-chip-clear]')?.addEventListener('click', () => {
    Object.assign(filter, {
      type: '', status: '', bucket: '', overdue: false, custom: false, booking_expired: false,
      agent_id: '', q: '', source: '', search_scope: '', focus: false,
    });
    if ('dept_scope' in filter) filter.dept_scope = 'my';
    const inp = document.getElementById('searchInput') || document.getElementById('leadsSearch');
    if (inp) inp.value = '';
    onChange();
  });
}

const PIPE_COLORS = {
  active: '#00AEEF',
  waiting_client: '#f59e0b',
  waiting_supplier: '#8A63D2',
  completed: '#3ddeb0',
  closed: '#94a3b8',
};

export function renderClickablePipeline(stats, onBucketClick) {
  const bar = document.getElementById('pipelineBar');
  const legend = document.getElementById('pipelineLegend');
  if (!bar) return;
  const total = stats.total || 0;
  const buckets = stats.by_bucket || {};
  const order = ['active', 'waiting_client', 'waiting_supplier', 'completed', 'closed'];

  bar.innerHTML = order.map((key) => {
    const c = buckets[key] || 0;
    if (!c || !total) return '';
    const pct = Math.max(2, (c / total) * 100);
    const label = STATUS_BUCKETS[key]?.label || key;
    return `<button type="button" class="pipeline-seg" data-bucket="${esc(key)}" style="width:${pct}%;background:${PIPE_COLORS[key]}" title="${esc(label)}: ${c} — click to filter" aria-label="Filter ${esc(label)}"></button>`;
  }).join('');

  bar.querySelectorAll('[data-bucket]').forEach((btn) => {
    btn.addEventListener('click', () => onBucketClick(`bucket:${btn.dataset.bucket}`));
  });

  if (legend) {
    legend.textContent = total
      ? order.filter((k) => buckets[k]).map((k) => `${STATUS_BUCKETS[k].label} ${buckets[k]}`).join(' · ')
      : 'No data yet';
  }
}

export function createLeadPrefetch(cache) {
  let timer = null;
  let lastId = null;

  return {
    schedule(id) {
      if (isLiteModeOn() || !id || cache.has(id)) return;
      lastId = id;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (lastId !== id) return;
        fetch(`/api/leads/${id}`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => { if (data) cache.set(id, data); })
          .catch(() => {});
      }, 200);
    },
    get(id) {
      return cache.get(id) || null;
    },
  };
}

export function initDetailSwipe(panel, overlay, onClose) {
  if (!panel || panel.dataset.swipeBound) return;
  panel.dataset.swipeBound = '1';
  let startX = 0;
  let startY = 0;
  panel.addEventListener('touchstart', (e) => {
    if (!panel.classList.contains('open')) return;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  }, { passive: true });
  panel.addEventListener('touchend', (e) => {
    if (!panel.classList.contains('open')) return;
    const dx = e.changedTouches[0].clientX - startX;
    const dy = e.changedTouches[0].clientY - startY;
    if (dx > 80 && Math.abs(dy) < 60) onClose();
  }, { passive: true });
}

/**
 * Marks a horizontally scrolling strip with .is-scrollable / .at-start / .at-end
 * so CSS can fade the clipped edge, and scrolls the active child into view.
 * Safe to call repeatedly (e.g. after every re-render).
 */
export function syncScrollStrip(el, activeSelector = '.active, .selected, [aria-current="step"]') {
  if (!el) return;
  el.classList.add('scroll-strip');
  const update = () => {
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    el.classList.toggle('is-scrollable', scrollable);
    el.classList.toggle('at-start', el.scrollLeft <= 1);
    el.classList.toggle('at-end', el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };
  if (!el.dataset.scrollStripBound) {
    el.dataset.scrollStripBound = '1';
    el.addEventListener('scroll', update, { passive: true });
    if (typeof ResizeObserver === 'function') new ResizeObserver(update).observe(el);
  }
  update();
  if (!activeSelector || !el.classList.contains('is-scrollable')) return;
  const active = el.querySelector(activeSelector);
  if (active && typeof active.scrollIntoView === 'function') {
    active.scrollIntoView({ block: 'nearest', inline: 'center' });
  }
}

export function initReduceEffectsToggle() {
  initLiteModeAssets();
}

export function isReduceEffectsOn() {
  return isLiteModeOn();
}

export function toggleReduceEffects() {
  const nextLite = !isLiteModeOn();
  setLiteMode(nextLite);
  return nextLite;
}

export function toggleLiteMode() {
  return toggleReduceEffects();
}
